import React, { useState, useEffect } from "react";
import {
  MapContainer,
  TileLayer,
  Marker,
  Polyline,
  Tooltip,
  SVGOverlay,
  Pane,
} from "react-leaflet";
// import { useMap } from "react-leaflet/hooks";
import "components/leaflet-area-select";
import L from "leaflet";

import SelectArea from "./SelectArea";
import { formatBubbleMetric } from "utils/map";
import MapControl from "./MapControl";

// AV-C3: well-spot symbols by class (industry / API-PPDM convention) with
// status colour coding, rendered as Leaflet divIcons (CSS, no image assets):
//   producer  -> filled green circle (oil/gas producing)
//   injector  -> blue circle with a downward arrow (injection / disposal)
//   dry       -> grey circle with an X (dry hole)
//   abandoned -> dark slashed circle (P&A / TA)
//   unknown   -> hollow grey circle (no fabricated classification)
//   selected  -> orange ring overlay on top of the class symbol
const WELL_SYMBOL_STYLE = {
  producer: { color: "#2e8b57", glyph: "" },
  injector: { color: "#1e6fd8", glyph: "▼" },
  dry: { color: "#808080", glyph: "✕" },
  abandoned: { color: "#3a3a3a", glyph: "／" },
  unknown: { color: "#9e9e9e", glyph: "" },
};

// Build a divIcon for a well. `size` is the production-scaled bubble diameter
// (px); selection draws an orange ring so the chosen well stays obvious.
function makeWellIcon(symbolClass, size, selected) {
  const style = WELL_SYMBOL_STYLE[symbolClass] || WELL_SYMBOL_STYLE.unknown;
  const d = Math.max(10, Math.min(60, Number(size) || 14));
  const filled = symbolClass === "producer" || symbolClass === "injector";
  const ring = selected ? "box-shadow:0 0 0 3px #ff8c00;" : "";
  const bg = filled ? style.color : "transparent";
  const fontSize = Math.max(8, Math.round(d * 0.55));
  const html =
    `<div style="width:${d}px;height:${d}px;border-radius:50%;` +
    `background:${bg};border:2px solid ${style.color};${ring}` +
    `display:flex;align-items:center;justify-content:center;` +
    `color:#fff;font-size:${fontSize}px;line-height:1;opacity:0.85;` +
    `box-sizing:border-box;">${style.glyph || ""}</div>`;
  return L.divIcon({
    className: "well-spot-icon",
    html,
    iconSize: [d, d],
    iconAnchor: [d / 2, d / 2],
  });
}

const LMap = (props) => {
  const {
    objectives,
    center,
    zoom,
    selectedObjectiveIds, // a Set
    bounds,
    objectiveSelectedHandler,
    areaSelectedHandler,
    showArrow,
    wellFilter,
  } = props;

  const [map, setMap] = useState(null);
  const [filteredWells, setFilteredWells] = useState(null);
  const [arrows, setArrows] = useState([]); // [[x1,y1,x2,y2],[x2,y2,x3,y3],...]

  useEffect(() => {
    if (!selectedObjectiveIds || !bounds) return;
    let arrowsArray = [];
    // get well obj
    let geoCoords = []; // [[lat1,lng1], [lat2, lng2], ...]

    [...selectedObjectiveIds].forEach((uwi) => {
      let resArr = objectives.filter((item) => item.apiId === uwi);
      if (!resArr || !resArr.length) return;
      let { Latitude, Longitude } = resArr[0];
      geoCoords.push([Latitude, Longitude]);
    });

    let bx1 = bounds.getSouthWest().lat,
      by1 = bounds.getSouthWest().lng,
      bx2 = bounds.getNorthEast().lat,
      by2 = bounds.getNorthEast().lng;

    // get the origin (Top left) and cornerBottomRight
    let origin = map.latLngToContainerPoint([bx2, by1]);
    let cornerBottomRight = map.latLngToContainerPoint([bx1, by2]);

    let width = Math.abs(origin.x - cornerBottomRight.x),
      height = Math.abs(origin.y - cornerBottomRight.y);

    for (let i = 0; i < geoCoords.length - 1; i++) {
      let x1, y1, x2, y2; // percentage
      let point1 = map.latLngToContainerPoint(geoCoords[i]);
      let point2 = map.latLngToContainerPoint(geoCoords[i + 1]);
      x1 = Math.abs(point1.x - origin.x)/width * 100 + "%";
      y1 = Math.abs(point1.y - origin.y)/height * 100 + "%";
      x2 = Math.abs(point2.x - origin.x)/width * 100 + "%";
      y2 = Math.abs(point2.y - origin.y)/height * 100 + "%";
      arrowsArray.push([x1, y1, x2, y2]);
    }
    // console.log("arrowsArray", arrowsArray);
    setArrows(arrowsArray);
  }, [objectives, selectedObjectiveIds, bounds, map]);

  useEffect(() => {
    // filter for wells, just for testing
    setFilteredWells(
      wellFilter
        ? objectives.filter((well) => wellFilter.includes(well.apiId))
        : objectives
    );
  }, [objectives, wellFilter]);

  return (
    <>
      <MapContainer
        className="map-container"
        center={center}
        zoom={zoom}
        ref={setMap}
        attributionControl={false}
      >
        <MapControl center={center} zoom={zoom} bounds={bounds} />
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <SelectArea
          Objectives={
            wellFilter
              ? objectives.filter((well) => wellFilter.includes(well.apiId))
              : objectives
          }
          selectedObjectivesHandler={areaSelectedHandler}
        />
        {/* AV-C3: horizontal-well bore trajectory (lateral). Drawn only when the
            well carries >=2 survey points; otherwise nothing here and the well
            shows as a wellhead spot only (graceful degradation for sparse
            Permian survey data). */}
        {filteredWells &&
          filteredWells.length > 0 &&
          filteredWells.map((objective, index) =>
            objective.Trajectory && objective.Trajectory.length >= 2 ? (
              <Polyline
                key={`traj-${index}`}
                positions={objective.Trajectory}
                pathOptions={{
                  color: selectedObjectiveIds?.has(objective.apiId)
                    ? "#ff8c00"
                    : "#444",
                  weight: 2,
                  opacity: 0.8,
                }}
              />
            ) : null
          )}
        {filteredWells &&
          filteredWells.length > 0 &&
          filteredWells.map((objective, index) => {
            const selected =
              selectedObjectiveIds && selectedObjectiveIds.has(objective.apiId);
            const metricText = formatBubbleMetric(
              objective.ProductType,
              objective.MetricValue,
              objective.MetricInfinite
            );
            return (
              <Marker
                key={index}
                zIndexOffset={selected ? 1000 : 0}
                icon={makeWellIcon(
                  objective.SymbolClass || "unknown",
                  objective.Value,
                  selected
                )}
                position={[objective.Latitude, objective.Longitude]}
                eventHandlers={{
                  click: () => {
                    objectiveSelectedHandler(objective);
                  },
                }}
              >
                <Tooltip
                  sticky
                  className="tooltip"
                  direction="top"
                  offset={[0, 0]}
                  opacity={1}
                  permanent
                >
                  <p className="fw-bold mb-0">{objective.Name}</p>
                  {metricText ? (
                    <p className="mb-0">{metricText}</p>
                  ) : null}
                </Tooltip>
              </Marker>
            );
          })}
        {showArrow && arrows && arrows.length > 0 && (
          <Pane name="arrows" style={{ zIndex: 1000 }}>
            <SVGOverlay bounds={bounds} zIndex={10000}>
              <defs>
                <marker
                  id="arrowhead"
                  markerWidth="10"
                  markerHeight="7"
                  refX="0"
                  refY="3.5"
                  orient="auto"
                >
                  <polygon points="0 0, 10 3.5, 0 7" fill="red" />
                </marker>
              </defs>
              {arrows.map(([x1, y1, x2, y2], index) => {
                console.log("x1, y1, x2, y2", x1, y1, x2, y2);
                return (
                  <line
                    key={index}
                    x1={x1}
                    y1={y1}
                    x2={x2}
                    y2={y2}
                    fill="red"
                    strokeWidth="3"
                    stroke="red"
                    markerEnd="url(#arrowhead)"
                  />
                );
              })}
            </SVGOverlay>
          </Pane>
        )}
      </MapContainer>
    </>
  );
};

export default LMap;
